/**
 * Root layout for auth gateway UI
 *
 * Card-on-page composition with a quiet Visor signature: the wordmark, a thin
 * highlight (gold) underline at brand width, and brand-tinted neutrals already
 * carried by theme tokens. Voice surfaces in copy, not chrome.
 */
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { QueryClientProvider, useSuspenseQuery } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRootRouteWithContext, Link, Outlet, useLocation } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { ThemeProvider } from "@better-bookkeeping/ui/components/theme-provider";
import { VisorWordmark } from "@better-bookkeeping/ui/visor-logo";
import type { RouterContext } from "@/router";
import { RuntimeConfigContext, runtimeConfigQueryOptions } from "@/lib/runtime-config";
import "../styles.css";
import { ScrollArea } from "@better-bookkeeping/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@better-bookkeeping/ui/alert";
import { AuthLoading } from "@/components/auth-loading";
import { INVITATION_CONTINUATION_SEARCH_PARAM } from "@/lib/auth-flow-redirects";
import {
  invitationEntryUrlWithoutCapability,
  preparePendingInvitationAuth,
  resumePendingInvitationAuth,
  withInviteTokenSearch,
} from "@/lib/pending-pricing-invite";

// AUTH-122: react-grab + DevToolbar are DEV-only. Lazy-loaded so the
// production bundle never contains them. The init module is a side-effect
// import that wires react-grab's defaults — must run after react-grab
// itself is loaded.
if (import.meta.env.DEV) {
  void import("react-grab").then(() => import("@better-bookkeeping/ui/dev-toolbar/init"));
}

const AuthDevToolbar = import.meta.env.DEV
  ? lazy(() => import("@/components/dev/dev-toolbar").then((m) => ({ default: m.AuthDevToolbar })))
  : () => null;

export const Route = createRootRouteWithContext<RouterContext>()({
  validateSearch: withInviteTokenSearch({}).parse,
  // AUTH-120: Load runtime config before any route renders so redirect-utils
  // and friends can rely on it being present synchronously.
  loader: ({ context }) => context.queryClient.ensureQueryData(runtimeConfigQueryOptions),
  component: RootComponent,
  errorComponent: ErrorComponent,
  notFoundComponent: NotFoundComponent,
});

function BrandHeader() {
  return (
    <div className="flex flex-col items-center gap-3 pt-8 pb-6">
      <VisorWordmark
        className="h-8 w-auto"
        classNameLetters="dark:fill-foreground"
        classNameVBottom="dark:fill-foreground"
        classNameVTop="dark:fill-foreground"
      />
      {/* Quiet brand signature — a hairline highlight tint, ~24px wide.
          Reads as intentional from the design system without raising the
          card's overall visual volume. */}
      <span aria-hidden="true" className="block h-px w-6 rounded-full bg-highlight/50" />
    </div>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { data: runtimeConfig } = useSuspenseQuery(runtimeConfigQueryOptions);
  // Signup is a marketing-style page that owns its own full-bleed split layout
  // and brand chrome, so it opts out of the centered auth card. (AUTH-164)
  const location = useLocation();
  const isMarketingLayout = location.pathname === "/signup";

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RuntimeConfigContext.Provider value={runtimeConfig}>
          {isMarketingLayout ? (
            <InvitationEntryBoundary key={location.searchStr} search={location.searchStr}>
              <Outlet />
            </InvitationEntryBoundary>
          ) : (
            <div className="min-h-dvh flex items-center justify-center bg-background p-6">
              <div className="w-full max-w-lg space-y-3">
                <ScrollArea
                  className="*:data-[slot=scroll-area-viewport]:max-h-[calc(100dvh-3rem)] bg-card rounded-lg shadow-sm"
                  fade={{ color: "var(--color-card)" }}
                >
                  <BrandHeader />
                  <div className="px-8 pb-8">
                    <InvitationEntryBoundary key={location.searchStr} search={location.searchStr}>
                      <Outlet />
                    </InvitationEntryBoundary>
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}

          {/* DevTools - only in development */}
          {import.meta.env.DEV && (
            <>
              <TanStackRouterDevtools position="bottom-right" />
              <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
              <Suspense fallback={null}>
                <AuthDevToolbar />
              </Suspense>
            </>
          )}
        </RuntimeConfigContext.Provider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

function InvitationEntryBoundary({ search, children }: { search: string; children: ReactNode }) {
  const [state, setState] = useState<"preparing" | "ready" | "error" | "limited">("preparing");

  useEffect(() => {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    const packageToken = params.get("invitation_package_token");
    const legacyToken = params.get("invite_token") ?? params.get("pricing_invite_token");
    const token = packageToken ? `package:${packageToken}` : legacyToken;
    const continuationId = params.get(INVITATION_CONTINUATION_SEARCH_PARAM);
    if (!token && !continuationId) {
      setState("ready");
      return;
    }

    const existingReferrerMeta = document.head.querySelector('meta[name="referrer"]');
    if (existingReferrerMeta instanceof HTMLMetaElement) {
      existingReferrerMeta.content = "no-referrer";
    } else {
      const referrerMeta = document.createElement("meta");
      referrerMeta.name = "referrer";
      referrerMeta.content = "no-referrer";
      document.head.append(referrerMeta);
    }

    let active = true;
    const preparation = token
      ? preparePendingInvitationAuth(token)
      : resumePendingInvitationAuth(continuationId ?? "");
    void preparation
      .then(() => {
        if (!active) return;
        window.location.replace(
          invitationEntryUrlWithoutCapability(
            window.location.pathname,
            window.location.search,
            window.location.hash,
          ),
        );
      })
      .catch((error: unknown) => {
        if (!active) return;
        const isRateLimited =
          error instanceof Error && "code" in error && error.code === "TOO_MANY_REQUESTS";
        setState(isRateLimited ? "limited" : "error");
      });
    return () => {
      active = false;
    };
  }, [search]);

  if (state === "preparing") return <AuthLoading data-testid="invitation-auth-preparing" />;
  if (state === "error" || state === "limited") {
    return (
      <Alert variant="destructive" data-testid="invitation-auth-error">
        <AlertTitle>Invitation could not be prepared</AlertTitle>
        <AlertDescription>
          {state === "limited"
            ? "Wait before trying again. If you have another invitation open, complete it first."
            : "Reopen the invitation link and try again."}
        </AlertDescription>
      </Alert>
    );
  }
  return children;
}

function ErrorComponent({ error }: { error: Error }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background p-6"
      data-testid="auth-error-state"
    >
      <div className="w-full max-w-md bg-card rounded-lg shadow-sm p-8 space-y-4 text-center">
        <h2 className="text-xl font-semibold text-destructive" data-testid="auth-error-title">
          That didn't go as planned.
        </h2>
        <p className="text-sm text-muted-foreground" data-testid="auth-error-message">
          {error.message}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          data-testid="auth-error-reload-button"
        >
          Reload and try again
        </button>
      </div>
    </div>
  );
}

function NotFoundComponent() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background p-6"
      data-testid="auth-not-found-state"
    >
      <div className="w-full max-w-md bg-card rounded-lg shadow-sm p-8 space-y-4 text-center">
        <h2 className="text-xl font-semibold text-foreground" data-testid="auth-not-found-title">
          Nothing lives here.
        </h2>
        <p className="text-sm text-muted-foreground">
          The page you're after doesn't exist on Visor's sign-in.
        </p>
        <Link
          to="/"
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 inline-block"
          data-testid="auth-not-found-home-link"
        >
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
